import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { get, useFormState } from 'react-hook-form';

import { hasDirtyLeaf } from './hasDirtyLeaf';

export type UseIsValidOptions<T extends FieldValues> = {
  control?: Control<T>;
  controlledTouchedFields?: FieldPath<T>[];
};

/**
 * Standalone hook that checks whether a set of form fields are in a "valid"
 * state — meaning each field has been interacted with (dirty or touched) and
 * has no validation errors.
 *
 * When `control` is not provided, `useFormState` internally falls back to
 * `useFormContext`, so this hook works both with explicit control and inside
 * a FormProvider.
 *
 * @example
 * // Pattern 1 — via FormProvider context (preferred):
 * const isValid = useIsValid<MyFormType>(['email', 'name']);
 *
 * // Pattern 2 — with explicit control:
 * const isValid = useIsValid<MyFormType>(['email'], { control: form.control });
 */
export const useIsValid = <T extends FieldValues>(
  fields: FieldPath<T>[],
  options?: UseIsValidOptions<T>,
): boolean => {
  const controlledTouched = options?.controlledTouchedFields ?? [];

  // useFormState internally calls useFormContext() as fallback when control
  // is not provided — no need to call useFormContext() explicitly here.
  const states = useFormState({ control: options?.control, name: fields });

  return fields.every((field) => {
    const error = get(states.errors, field);
    const isTouched = !!get(states.touchedFields, field);

    // Read isValidating from control's internal state (_formState) rather
    // than through the useFormState proxy (states.validatingFields).
    // This matches RHF's getFieldState() which reads _formState directly,
    // avoiding a proxy subscription that would cause extra re-renders
    // during async validation.
    const isValidating = options?.control
      ? !!get(options.control._formState.validatingFields, field)
      : false;

    if (!!error || isValidating) {
      return false;
    }

    // RHF's getFieldState().isDirty uses `!!dirtyFields[field]` which
    // false-positives for array/object fields. Walk the dirtyFields tree
    // to check if any leaf is actually `true`.
    const rawDirtyValue = get(states.dirtyFields, field);
    const isDirty = hasDirtyLeaf(rawDirtyValue);

    return controlledTouched.includes(field) || isDirty || isTouched;
  });
};
